import React from "react";
import { Tag, Collapse, Empty, Typography } from "antd";
import moment from "moment";
import {
  FileTextOutlined,
  CarOutlined,
  BookOutlined,
  FormOutlined,
  FileDoneOutlined,
  DollarOutlined,
  HistoryOutlined,
  BellOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  AuditOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { Panel } = Collapse;

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val) => (val ? String(val) : null);
const fmtDate = (val) => (val ? moment(val).format("DD/MM/YYYY") : null);
const fmtDateTime = (val) => (val ? moment(val).format("DD/MM/YYYY HH:mm") : null);

export const getIdentifier = (module, item) => {
  switch (module) {
    case "vehicles":
    case "purchasedVehicle":
      return {
        primary: fmt(item.registerNo) || fmt(item.chassisNo) || "—",
        secondary:
          item.chassisNo && item.registerNo
            ? `Chassis: ${item.chassisNo}`
            : item.engineNo
            ? `Engine: ${item.engineNo}`
            : null,
      };
    case "jobOrders":
      return {
        primary: fmt(item.jobNo) || "—",
        secondary: item.dateTime ? fmtDate(item.dateTime) : null,
      };
    case "spareOrders":
      return {
        primary: fmt(item.orderNumber) || "—",
        secondary: item.orderDate ? fmtDate(item.orderDate) : null,
      };
    case "estimates":
      return {
        primary: fmt(item.estimateNo) || "—",
        secondary: item.dateTime ? fmtDate(item.dateTime) : fmt(item.serviceType),
      };
    case "saleInvoices":
      return {
        primary: fmt(item.invoiceNo) || fmt(item.saleInvoiceNo) || "—",
        secondary: item.date ? fmtDate(item.date) : item.createdAt ? fmtDate(item.createdAt) : null,
      };
    case "saleSpareInvoices":
      return {
        primary: fmt(item.invoiceNumber) || "—",
        secondary: item.invoiceDate ? fmtDate(item.invoiceDate) : item.createdAt ? fmtDate(item.createdAt) : null,
      };
    case "payments": {
      const mainId = fmt(item.receiptId) || fmt(item.bookingId) || fmt(item.transactionId) || fmt(item.id) || "Receipt";
      const amt = (item.collectedAmount || item.amount) ? ` (₹${Number(item.collectedAmount || item.amount).toLocaleString()})` : "";
      return {
        primary: `${mainId}${amt}`,
        secondary: item.dateOfPayment
          ? fmtDate(item.dateOfPayment)
          : item.createdAt
          ? fmtDate(item.createdAt)
          : item.method
          ? fmt(item.method)
          : null,
      };
    }
    case "bookingsDirect":
    case "bookingsM2M":
    case "booking":
      return {
        primary:
          fmt(item.bookingId) || fmt(item.confirmBookingId) || "—",
        secondary: item.createdAt ? fmtDate(item.createdAt) : null,
      };
    case "quotationsDirect":
    case "quotationsM2M":
    case "quotation":
      return {
        primary: fmt(item.quotationId) || "—",
        secondary: item.createdAt
          ? fmtDate(item.createdAt)
          : fmt(item.enquiryType),
      };
    case "activitiesDirect":
    case "activitiesM2M":
      return {
        primary: fmt(item.activityId) || "—",
        secondary: fmt(item.activityType) || fmtDateTime(item.createdAt),
      };
    case "digitalLeads":
      return {
        primary:
          [fmt(item.name), fmt(item.phone)].filter(Boolean).join(" - ") || "—",
        secondary: fmt(item.enquiryType),
      };
    case "otherInsuranceReminders":
      return {
        primary: fmt(item.policyNumber) || fmt(item.registerNo) || "—",
        secondary:
          fmt(item.insuranceType) ||
          (item.expiryDate ? `Expiry: ${fmtDate(item.expiryDate)}` : null),
      };
    case "cloudCallHistory":
      return {
        primary: fmt(item.callId) || "—",
        secondary: item.startTime ? `Started: ${fmt(item.startTime)}` : null,
      };
    case "cashFlows":
      return {
        primary: fmt(item.receiptId) || fmt(item.transactionId) || "—",
        secondary: item.dateOfPayment
          ? fmtDate(item.dateOfPayment)
          : fmt(item.paymentMode),
      };
    case "multiCustomers":
      return {
        primary: "Multi-Customer Link",
        secondary: item.createdAt ? fmtDate(item.createdAt) : null,
      };
    case "groups":
      return { primary: "Group Linked", secondary: null };
    default:
      return { primary: fmt(item.id) || "—", secondary: null };
  }
};

export const MODULE_CONFIG = [
  { key: "vehicles",                label: "Vehicles",             icon: <CarOutlined /> },
  { key: "jobOrders",               label: "Job Cards",            icon: <FileTextOutlined /> },
  { key: "spareOrders",             label: "Spare Orders",         icon: <FileDoneOutlined /> },
  { key: "estimates",               label: "Estimates",            icon: <FormOutlined /> },
  { key: "saleInvoices",            label: "Sale Invoices",        icon: <FileDoneOutlined /> },
  { key: "saleSpareInvoices",       label: "Counter Sales",        icon: <FileDoneOutlined /> },
  { key: "payments",                label: "Payments",             icon: <DollarOutlined /> },
  { key: "bookingsDirect",          label: "Bookings",             icon: <BookOutlined /> },
  { key: "bookingsM2M",             label: "Bookings (Linked)",    icon: <BookOutlined /> },
  { key: "quotationsDirect",        label: "Quotations",           icon: <AuditOutlined /> },
  { key: "quotationsM2M",           label: "Quotations (Linked)",  icon: <AuditOutlined /> },
  { key: "activitiesDirect",        label: "Activities",           icon: <HistoryOutlined /> },
  { key: "activitiesM2M",           label: "Activities (Linked)",  icon: <HistoryOutlined /> },
  { key: "digitalLeads",            label: "Digital Leads",        icon: <TeamOutlined /> },
  { key: "otherInsuranceReminders", label: "Insurance Reminders",  icon: <BellOutlined /> },
  { key: "cloudCallHistory",        label: "Call History",         icon: <CustomerServiceOutlined /> },
  { key: "cashFlows",               label: "Cash Flows",           icon: <DollarOutlined /> },
  { key: "multiCustomers",          label: "Multi-Customer Links", icon: <TeamOutlined /> },
  { key: "groups",                  label: "Groups",               icon: <TeamOutlined /> },
];

// Live customer data keys (from fetchCustomerDetails response) mapped to MODULE_CONFIG keys
// Used in Confirm Dialog where we have live data (not auditData snapshot)
// Keys that have the SAME name in the API and in MODULE_CONFIG do not need a mapping entry —
// they resolve automatically. Only list entries where the names DIFFER.
const LIVE_KEY_MAP = {
  purchasedVehicle: "vehicles",       // API key → panel key
  booking:          "bookingsDirect",
  quotation:        "quotationsDirect",
  activities:       "activitiesDirect",
};

/**
 * ModuleBreakdownPanels
 *
 * @param {object}  snapshot   - Either auditData.fromCustomer (from audit) OR live fromCustomer (from fetchCustomerDetails)
 * @param {boolean} isLiveData - true = live customer object, false = auditData snapshot
 */
export default function ModuleBreakdownPanels({ snapshot, isLiveData = false }) {
  if (!snapshot) return <Empty description="No data available" />;

  // Resolve the data source for each module
  const getItems = (moduleKey) => {
    if (isLiveData) {
      // Map live keys to module keys
      const liveKey = Object.keys(LIVE_KEY_MAP).find(
        (k) => LIVE_KEY_MAP[k] === moduleKey
      );
      const directKey = liveKey || moduleKey;
      return snapshot[directKey] || [];
    }
    return snapshot[moduleKey] || [];
  };

  const activeModules = MODULE_CONFIG.filter(({ key }) => {
    const items = getItems(key);
    return Array.isArray(items) && items.length > 0;
  });

  if (activeModules.length === 0) {
    return (
      <Empty
        description="No module records to transfer"
        style={{ marginTop: 24 }}
      />
    );
  }

  return (
    <Collapse
      defaultActiveKey={activeModules.map((m) => m.key)}
      bordered={false}
      style={{ background: "transparent" }}
    >
      {activeModules.map(({ key, label, icon }) => {
        const items = getItems(key);
        return (
          <Panel
            key={key}
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#006a71", fontSize: 15 }}>{icon}</span>
                <Text strong style={{ fontSize: 13, color: "#2d3748" }}>
                  {label}
                </Text>
                <Tag
                  style={{
                    marginLeft: "auto",
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 600,
                    backgroundColor: "#e6f7ff",
                    color: "#1890ff",
                    borderColor: "#91d5ff",
                  }}
                >
                  {items.length} {items.length === 1 ? "record" : "records"}
                </Tag>
              </div>
            }
            style={{
              background: "#f8fafc",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              marginBottom: 8,
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 2 }}>
              {items.map((item, idx) => {
                const { primary, secondary } = getIdentifier(key, item);
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 4,
                      padding: "7px 12px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: "#006a71",
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                      <Text strong style={{ fontSize: 13, color: "#1a202c" }}>
                        {primary}
                      </Text>
                    </div>
                    {secondary && (
                      <Text style={{ fontSize: 12, color: "#718096" }}>
                        {secondary}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>
        );
      })}
    </Collapse>
  );
}
